import { useEffect } from "react";
import { useForm, Controller, FieldError } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FormText from "@/components/kit/FormGenerator/FormText";
import AsyncPlanSelect from "./AsyncPlanSelect";
import { ManualSubscriptionFormType } from "./form-type";
import { manualSubscriptionValidationSchema } from "./validation";

function getDefaultStartFrom() {
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    // Format as 'YYYY-MM-DDTHH:mm' for datetime-local input
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

const defaultValues: ManualSubscriptionFormType = {
    userId: "",
    planId: "",
    startFrom: getDefaultStartFrom(),
};

interface ManualSubscriptionFormProps {
    loading: boolean;
    onSubmit: (data: ManualSubscriptionFormType, resetForm: () => void) => void;
}

const ManualSubscriptionForm: React.FC<ManualSubscriptionFormProps> = ({ loading, onSubmit }) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        trigger,
    } = useForm<ManualSubscriptionFormType>({
        defaultValues,
        mode: "all",
        resolver: yupResolver(manualSubscriptionValidationSchema),
    });

    useEffect(() => {
        if (isValid) void trigger();
    }, [trigger, isValid]);

    return (
        <form
            style={{ maxWidth: "450px", margin: "0 auto", padding: "0 0 2em 0" }}
            onSubmit={handleSubmit((values) => onSubmit(values, () => reset(defaultValues)))}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Controller
                        name="userId"
                        control={control}
                        render={({ field }) => (
                            <FormText
                                field={field as any}
                                label="User ID"
                                placeholder="Enter user ID"
                                errorMessage={errors.userId as FieldError}
                                props={{ type: "text" }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="planId"
                        control={control}
                        render={({ field }) => (
                            <AsyncPlanSelect
                                value={field.value}
                                onChange={field.onChange}
                                label="Plan"
                                error={errors.planId?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="startFrom"
                        control={control}
                        render={({ field }) => (
                            <FormText
                                field={field as any}
                                label="Start From"
                                placeholder="Select date and time"
                                errorMessage={errors.startFrom as FieldError}
                                props={{ type: "datetime-local" }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton
                        className="loading-btn"
                        disabled={!isValid || loading}
                        type="submit"
                        variant="contained"
                        fullWidth
                        loading={loading}
                    >
                        Submit
                    </LoadingButton>
                </Grid>
            </Grid>
        </form>
    );
};

export default ManualSubscriptionForm;
